import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import UnifiedInquirySection from '../../components/features/UnifiedInquirySection';
import ShareButton from '../../components/common/ShareButton';
import { getNote } from '../../api/notes';
import './Notes.css';

/**
 * 기술노트 상세.
 *
 * 본문 HTML 은 **서버가 만들어 보낸다**(`renderMarkdown`). 클라이언트에서 마크다운을
 * 파싱하지 않는 이유는 두 가지다 — 같은 본문이 SSR 응답에도 실려야 네이버 수집기가
 * 글을 볼 수 있고, 소독(sanitize)을 서버 한 곳에서만 하면 규칙이 어긋나지 않는다.
 *
 * JSON-LD 도 서버가 만든 것을 그대로 심는다. 구조화 데이터를 붙이는 이유는 리치
 * 결과보다 **AI 검색**이다 — Article 의 저자·발행일과 FAQPage 의 문답이 그대로
 * 인용 단위가 된다.
 */
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' }) : '');

const NoteDetail = ({ slug: slugProp }) => {
    /*
     * 라우트가 `/notes/:segment` 라 파라미터 이름이 `segment` 다. 이 컴포넌트를
     * `slug` 로만 읽게 두면 항상 undefined 가 되어 "찾을 수 없습니다"가 뜬다 —
     * 실제로 그랬다. 호출부가 넘겨 준 값을 먼저 쓰고, 직접 라우팅된 경우를 위해
     * 두 이름을 모두 본다.
     */
    const params = useParams();
    const slug = slugProp ?? params.slug ?? params.segment;
    const navigate = useNavigate();
    const [note, setNote] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true); setError('');
        getNote(slug)
            .then((d) => { if (alive) setNote(d); })
            .catch(() => { if (alive) setError('기술노트를 찾을 수 없습니다.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [slug]);

    // 제목·설명은 SSR 이 이미 심어 두지만, 앱 안에서 이동해 온 경우에는 그 값이
    // 이전 페이지 것이므로 여기서 다시 맞춘다.
    useEffect(() => {
        if (!note) return;
        document.title = `${note.metaTitle || note.title} | 가까운솔루션`;
        const desc = document.querySelector('meta[name="description"]');
        const prev = desc?.getAttribute('content');
        if (desc && (note.metaDescription || note.summary)) {
            desc.setAttribute('content', note.metaDescription || note.summary);
        }
        return () => {
            document.title = '가까운솔루션';
            if (desc && prev != null) desc.setAttribute('content', prev);
        };
    }, [note]);

    // JSON-LD 는 서버가 만든 것을 그대로 심는다.
    useEffect(() => {
        if (!note?.jsonLd) return;
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.dataset.note = note.slug;
        el.textContent = JSON.stringify(note.jsonLd);
        document.head.appendChild(el);
        return () => { el.remove(); };
    }, [note]);

    if (loading) return <div className="notes-page"><div className="container notes-empty">불러오는 중…</div></div>;
    if (error || !note) {
        return (
            <div className="notes-page">
                <div className="container notes-empty">
                    <p>{error}</p>
                    <button type="button" className="note-back" onClick={() => navigate('/notes')}>← 기술노트 목록</button>
                </div>
            </div>
        );
    }

    return (
        <article className="note-detail">
            <header className="note-head">
                <div className="container">
                    <nav className="note-crumbs" aria-label="현재 위치">
                        <Link to="/notes">기술노트</Link>
                        {note.categorySlug && (
                            <>
                                <span aria-hidden="true">›</span>
                                <Link to={`/notes/${note.categorySlug}`}>{note.categoryLabel}</Link>
                            </>
                        )}
                    </nav>
                    <h1 className="note-title">{note.title}</h1>
                    {note.subtitle && <p className="note-subtitle">{note.subtitle}</p>}
                    <div className="note-meta">
                        {note.authorName && (
                            <span className="note-author">
                                {note.authorName}
                                {note.authorRole && <em>{note.authorRole}</em>}
                            </span>
                        )}
                        {note.publishedAt && <time dateTime={note.publishedAt}>{fmtDate(note.publishedAt)}</time>}
                        {note.contentUpdatedAt && note.contentUpdatedAt !== note.publishedAt && (
                            <span className="note-updated">수정 {fmtDate(note.contentUpdatedAt)}</span>
                        )}
                    </div>
                </div>
            </header>

            <div className="container note-layout">
                <div className="note-main">
                    {/* AI 검색이 인용하는 단위. 본문 맨 앞에 둔다(방향서 §6-4). */}
                    {note.definition && (
                        <aside className="note-definition">
                            <h2 className="note-definition-title">한 문단 요약</h2>
                            <p>{note.definition}</p>
                        </aside>
                    )}

                    <div
                        className="note-body"
                        /* 서버가 sanitize 한 HTML 이다. 클라이언트에서 다시 파싱하지 않는다. */
                        dangerouslySetInnerHTML={{ __html: note.bodyHtml }}
                    />

                    {note.faqs?.length > 0 && (
                        <section className="note-faq">
                            <h2>자주 묻는 질문</h2>
                            <dl>
                                {note.faqs.map((f, i) => (
                                    <div key={i} className="note-faq-item">
                                        <dt>{f.question}</dt>
                                        <dd>{f.answerMd}</dd>
                                    </div>
                                ))}
                            </dl>
                        </section>
                    )}

                    {note.related?.length > 0 && (
                        <section className="note-related">
                            <h2>함께 보면 좋은 글</h2>
                            <ul>
                                {note.related.map((r) => (
                                    <li key={r.slug}>
                                        <Link to={r.type === 'note' ? `/notes/${r.slug}` : `/${r.type}s/${r.slug}`}>
                                            {r.title}
                                        </Link>
                                        {r.summary && <p>{r.summary}</p>}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <div className="note-foot">
                        <ShareButton
                            title={note.title}
                            description={note.summary || ''}
                            url={`${window.location.origin}/notes/${note.slug}`}
                        />
                        <Link to="/notes" className="note-back">← 기술노트 목록</Link>
                    </div>
                </div>

                {note.headings?.length > 2 && (
                    <nav className="note-toc" aria-label="목차">
                        <p className="note-toc-title">목차</p>
                        <ul>
                            {note.headings.map((h) => (
                                <li key={h.id} className={h.level === 3 ? 'is-sub' : ''}>{h.text}</li>
                            ))}
                        </ul>
                    </nav>
                )}
            </div>

            <UnifiedInquirySection />
        </article>
    );
};

export default NoteDetail;
